import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { useQuestionnaireStore } from 'store/questionnaire';
import { checkSpanishLocale, cn } from 'lib/utils';
import Icon from 'components/icon/Icon';
import Checkmark from 'pages/profile/questionnaire/components/Checkmark';
import useQuestionnaire from 'hooks/useQuestionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';

const bodyTypeIconMapper = {
  Thin: 'thin',
  Average: 'average',
  Overweight: 'overweight',
  Obese: 'obese',
};

export default function BodyComplexion() {
  const { t } = useTranslation();
  const questionData = useQuestionnaireStore((store) => store.questionData);
  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const englishChoices = JSON.parse(questionData?.eng_choices || '{}');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '{}');

  const [selectedBodyIndex, setSelectedBodyIndex] = useState<number>(0);
  const [selectedMuscleIndex, setSelectedMuscleIndex] = useState<number>(0);

  const bodyTypes: { en_value: string; es_value: string; icon: string }[] =
    useMemo(
      () =>
        englishChoices && englishChoices?.length
          ? englishChoices?.map((choice: string, index: number) => ({
              en_value: choice,
              es_value: spanishChoices[index],
              icon: bodyTypeIconMapper[
                choice as keyof typeof bodyTypeIconMapper
              ],
            }))
          : [],
      [englishChoices, spanishChoices]
    );

  const muscles = useMemo(
    () => [t('dont_have_muscles'), t('have_muscles')],
    []
  );

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const labels = bodyTypes.map(
        (langLevel) => langLevel?.[isSpanish ? 'es_value' : 'en_value']
      );
      const userChoices =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      let answer = '';
      try {
        const parsedChoices = JSON.parse(userChoices);
        answer = parsedChoices[0];
      } catch (error) {
        console.log(error);
      }
      const splitAnswer = answer.split('-');
      const selectedType = splitAnswer?.[0];
      const muscleIndex = splitAnswer.length > 1 ? 1 : 0;
      const index = labels.indexOf(selectedType);
      setSelectedBodyIndex(index);
      setSelectedMuscleIndex(muscleIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);

  const onContinue = () => {
    const bodyTypeItem = bodyTypes[selectedBodyIndex];
    onSubmit({
      choice_value: JSON.stringify([
        selectedMuscleIndex === 0
          ? bodyTypeItem.en_value
          : `${bodyTypeItem.en_value}-muscles`,
      ]),
      spanish_choice_value: JSON.stringify([
        selectedMuscleIndex === 0
          ? bodyTypeItem.es_value
          : `${bodyTypeItem.es_value}-músculo`,
      ]),
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={1}
    >
      <div className='relative flex flex-wrap items-center gap-20'>
        {bodyTypes?.map((body, index) => {
          const isSelected = selectedBodyIndex === index;

          return (
            <div
              key={body.en_value}
              className='relative grid cursor-pointer place-items-center'
              onClick={() => setSelectedBodyIndex(index)}
            >
              {isSelected && (
                <motion.div
                  layoutId='body-type-indicator'
                  className='absolute z-0 w-16 h-16 top-0 rounded-[24px] border-[2px] border-primary bg-[#E1EBFF]'
                  transition={{
                    type: 'spring',
                    stiffness: 350,
                    damping: 40,
                  }}
                />
              )}

              <div
                className={cn(
                  'relative z-10 grid border-[2px] place-items-center size-16 rounded-[24px]',
                  isSelected && 'border-transparent'
                )}
              >
                <Icon
                  icon={body.icon}
                  size={40}
                  color={isSelected ? '#10A4B1' : '#808080'}
                />
              </div>

              <p
                className={cn(
                  'font-medium capitalize text-center mt-2 text-sm z-10',
                  isSelected ? 'text-primary' : 'text-gray-400'
                )}
              >
                {body.en_value} Body Type
              </p>
            </div>
          );
        })}
      </div>

      <div className='w-full h-px my-4 bg-gray-400' />

      <div className='space-y-3'>
        <p className='text-base font-semibold'>
          Would you describe yourself as muscular?
        </p>
        <div className='space-y-2'>
          <div className='grid grid-cols-2 gap-6'>
            {muscles.map((muscle, index) => {
              const isSelected = selectedMuscleIndex === index;
              return (
                <motion.div
                  key={muscle}
                  className={cn(
                    'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76]',
                    isSelected && 'border-primary text-primary'
                  )}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedMuscleIndex(index)}
                >
                  <p className='text-sm font-medium'>{muscle}</p>
                  <Checkmark isSelected={isSelected} />
                </motion.div>
              );
            })}
          </div>
          <div className='flex items-center gap-2'>
            <Icon icon='info' size={18} color='#CBD5E1' />
            <p className='pt-1 text-sm font-medium text-gray-400'>
              Muscles make the body heavier than it looks
            </p>
          </div>
        </div>
      </div>
    </QuestionnaireContainer>
  );
}
